/**
 * src/features/auth/lib/catalogOverridesLoader.ts
 *
 * Auth-triggered fetchers that apply server catalog data to the local service
 * store after login.
 *
 * Placed in features/auth (not features/sync) because they are auth-triggered —
 * called once after session establishment in useAuth (FSD rule: imports only
 * downward; same-layer cross-feature imports are prohibited, WR-03 fix).
 *
 * CAT-01: after login (on any device) the catalog reflects the user's server overrides.
 * CAT-03: after login (on any device) custom services are available across all devices.
 *
 * HARDEN-03: these loaders now throw HttpError on non-2xx responses and propagate
 * AbortError (timeout) — making them retryable by withRetry in useAuth. The
 * guest-first no-throw invariant is preserved at the withRetry call site (useAuth),
 * not in the loaders themselves.
 *
 * Threat mitigations:
 *  T-12-05 — *ResponseSchema.safeParse validates each response body at the fetch
 *             boundary (V5 input validation). Invalid shape → store action is NOT
 *             called; safeParse failures are still swallowed (not retryable).
 *  T-12-06 — AbortSignal.timeout(10_000) caps all requests; AbortError is now
 *             propagated (not swallowed) so withRetry can retry transient timeouts.
 *  T-28-03-01..03 — retry storm / availability / 4xx fast-fail are all handled by
 *             withRetry in useAuth (the call site).
 */

import { CatalogOverridesResponseSchema, UserServicesResponseSchema } from '@/shared/validation/schemas';
import { HttpError } from '@/shared/lib/retry';
import { useServiceStore } from '@/stores/useServiceStore';

/**
 * Loads catalog overrides from the server and applies them to the local service store.
 *
 * Throws HttpError on non-2xx responses (so withRetry in useAuth can retry 5xx).
 * Propagates AbortError (so withRetry can retry timeouts).
 * Invalid-shape (Zod safeParse) failures are still swallowed — they are not retryable.
 */
export async function loadCatalogOverrides(): Promise<void> {
  const res = await fetch('/api/catalog/overrides', {
    method: 'GET',
    signal: AbortSignal.timeout(10_000),
  });

  // Non-ok response (401 expired session, 403, 500, etc.) — throw typed error
  // so withRetry in useAuth can discriminate: 4xx → fail fast, 5xx → retry.
  if (!res.ok) {
    throw new HttpError(res.status, `GET /api/catalog/overrides → ${res.status}`);
  }

  const body: unknown = await res.json();

  // V5: validate at the fetch boundary — reject any unexpected shape (T-12-05).
  // Invalid shape is not retryable — swallow silently and keep current catalog state.
  const parsed = CatalogOverridesResponseSchema.safeParse(body);
  if (!parsed.success) return;

  // Apply the server-authoritative delta to rawServices (idempotent, partial merge).
  useServiceStore.getState().applyServerOverrides(parsed.data.overrides);
}

/**
 * Loads custom services from the server and applies them to the local service store.
 *
 * CAT-03: after login (on any device) custom services are available automatically.
 * Race-safe with migrateCatalogToCloud: applyCustomServices (13-03) merges local-only
 * customs so concurrent ordering with the migration cannot drop in-flight services
 * (Pitfall 5 — merge variant).
 *
 * Throws HttpError on non-2xx responses (so withRetry in useAuth can retry 5xx).
 * Propagates AbortError (so withRetry can retry timeouts).
 * Invalid-shape (Zod safeParse) failures are still swallowed — they are not retryable.
 *
 * Threat mitigations:
 *  T-12-05 — UserServicesResponseSchema.safeParse validates the response body at the
 *             fetch boundary (V5 input validation). Invalid shape → applyCustomServices
 *             is NOT called, no partial/malformed data applied.
 *  T-12-06 — AbortSignal.timeout(10_000) caps the request; AbortError is now
 *             propagated (not swallowed) so withRetry can retry transient timeouts.
 */
export async function loadCustomServices(): Promise<void> {
  const res = await fetch('/api/catalog/services', {
    method: 'GET',
    signal: AbortSignal.timeout(10_000),
  });

  // Non-ok response (401 expired session, 403, 500, etc.) — throw typed error
  // so withRetry in useAuth can discriminate: 4xx → fail fast, 5xx → retry.
  if (!res.ok) {
    throw new HttpError(res.status, `GET /api/catalog/services → ${res.status}`);
  }

  const body: unknown = await res.json();

  // V5: validate at the fetch boundary — reject any unexpected shape (T-12-05).
  // Invalid shape is not retryable — swallow silently and keep current catalog state.
  const parsed = UserServicesResponseSchema.safeParse(body);
  if (!parsed.success) return;

  // Apply the server-authoritative custom services (merge-safe, Pitfall 5).
  useServiceStore.getState().applyCustomServices(parsed.data.services);
}
